import { useQuery } from "@tanstack/react-query";
import { accountBinds, channelBinds } from "./binds";
import { channelHealth, primaryBackup } from "./health";
import { accounts, customers, type Account, type Resource } from "./master";
import { testSend } from "./testSend";

/** 通道健康度（metrics 每 10 秒建一次快照），健康度页与通道配置列表共用，查询键相同，只拉一份。 */
export function useChannelHealth() {
  return useQuery({ queryKey: ["channel-health"], queryFn: channelHealth.list, refetchInterval: 10_000 });
}

/** 通道的绑定状态（connector 每 5 秒上报一次心跳，决策 D176），通道配置列表用。 */
export function useChannelBinds() {
  return useQuery({ queryKey: ["channel-binds"], queryFn: channelBinds.list, refetchInterval: 10_000 });
}

/** 客户账号的在线会话（smpp-server 每 5 秒上报一次心跳，决策 D178），客户账号列表用。 */
export function useAccountBinds() {
  return useQuery({ queryKey: ["account-binds"], queryFn: accountBinds.list, refetchInterval: 10_000 });
}

/** 主备组的状态（metrics 每分钟判定一次），通道组合页用。 */
export function usePrimaryBackup() {
  return useQuery({ queryKey: ["primary-backup"], queryFn: primaryBackup.list, refetchInterval: 10_000 });
}

/** 表格要不要显示加载动画（TableWrap 的 loading）：首次加载，或换了条件、还显示着上一次的占位数据时；后台轮询与手动刷新时表格内容照旧（#541，决策 D199）。 */
export function loadingOf(q: { isLoading: boolean; isFetching: boolean; isPlaceholderData: boolean }) {
  return q.isLoading || (q.isFetching && q.isPlaceholderData);
}

/** 一种实体的列表查询，查询键是 [实体名, 过滤条件]，保存后按实体名整体失效。 */
export function useList<T, In>(key: string, res: Resource<T, In>, query?: Record<string, string>) {
  return useQuery({ queryKey: [key, query ?? {}], queryFn: () => res.list(query) });
}

/** 按 id 找名称，列表未加载或找不到时显示 id。 */
export function nameOf<T extends { id: number; name: string }>(list: T[] | undefined, id: number): string {
  return list?.find((x) => x.id === id)?.name ?? `#${id}`;
}

/** 列表转 Select 选项，value 是 id 的字符串。 */
export function idOptions<T extends { id: number; name: string }>(list: T[] | undefined) {
  return (list ?? []).map((x) => ({ value: String(x.id), label: x.name, hint: `#${x.id}` }));
}

/** 账号的 Select 选项：名称后带所属客户，提示是 system_id，同名账号也能分清。 */
export function accountOptions(list: Account[] | undefined, customerRows?: { id: number; name: string }[]) {
  return (list ?? []).map((a) => ({ value: String(a.id), label: `${a.name}（${nameOf(customerRows, a.customer_id)}）`, hint: a.system_id }));
}

/** 只展示名称的实体选项；ID、账号 system_id 与所属客户仅用于搜索（价格页面）。 */
export function nameOptions<T extends { id: number; name: string; system_id?: string; customer_id?: number }>(list: T[] | undefined, customerRows?: { id: number; name: string }[]) {
  return (list ?? []).map((x) => ({
    value: String(x.id), label: x.name,
    search: [x.name, `#${x.id}`, x.system_id, customerRows?.find((c) => c.id === x.customer_id)?.name].filter(Boolean).join(" "),
  }));
}

/** 系统测试账号与客户的显示名（决策 D94）。 */
export const SYSTEM_TEST_NAME = "系统测试";

/** 按 id 显示账号与客户的名称：系统测试账号与客户不在主数据列表里，显示成"系统测试"而不是 #id。列表与系统 id 各查一次，与页面共用查询键。 */
export function useNames() {
  const accountList = useList("accounts", accounts);
  const customerList = useList("customers", customers);
  const system = useQuery({ queryKey: ["test-send-system"], queryFn: testSend.system, staleTime: Infinity });
  return {
    account: (id: number) => (id === system.data?.account_id ? SYSTEM_TEST_NAME : nameOf(accountList.data, id)),
    customer: (id: number) => (id === system.data?.customer_id ? SYSTEM_TEST_NAME : nameOf(customerList.data, id)),
  };
}
